import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useRoute } from "wouter";
import { ArrowLeft } from "lucide-react";
import { api, formatMoney } from "../lib/api.js";
import { logApiError, stripeCheckoutLoadErrorMessage, stripeCheckoutLoadErrorDetail } from "../lib/apiErrors.js";
import { StripeEmbeddedCheckout } from "../components/StripeEmbeddedCheckout.js";
import { RenewalTerms } from "../components/RenewalTerms.js";
import {
  buildStripeBackUrl,
  loadStripeCheckoutContext,
  loadStripeCheckoutSession,
  saveStripeCheckoutContext,
  saveStripeCheckoutSession,
  type StripeCheckoutContext,
} from "../lib/stripeCheckout.js";
import { loadPersistedCheckoutOfferToken } from "../lib/checkoutOfferSession.js";
import type { BillingInterval, Plan } from "../../../shared/types.js";

interface StripeCheckoutPayload {
  order_id: string;
  client_secret: string;
  publishable_key: string | null;
  final_cents: number;
  renewal_cents?: number;
  first_charge_at?: string | null;
  context_kind?: string;
  plan?: Plan;
  interval?: BillingInterval;
  email?: string;
}

function resolveBackUrl(orderId: string, payload?: StripeCheckoutPayload | null): string {
  const stored = loadStripeCheckoutContext(orderId);
  if (stored) return buildStripeBackUrl(stored);

  const params = new URLSearchParams(window.location.search);
  const integratedToken = params.get("token");
  if (params.get("from") === "integrated") {
    const ctx: StripeCheckoutContext = {
      orderId,
      plan: payload?.plan ?? "premium",
      interval: payload?.interval ?? "monthly",
      email: payload?.email ?? "",
      returnTo: "integrated",
      integratedToken: integratedToken ?? undefined,
    };
    return buildStripeBackUrl(ctx);
  }

  if (payload?.plan && payload.interval && payload.email) {
    const ctx: StripeCheckoutContext = {
      orderId,
      plan: payload.plan,
      interval: payload.interval,
      email: payload.email,
      offerToken: loadPersistedCheckoutOfferToken() || undefined,
    };
    saveStripeCheckoutContext(ctx);
    return buildStripeBackUrl(ctx);
  }

  return "/plans";
}

/**
 * Top-level Embedded Checkout page.
 *
 * Opened after the customer form is submitted (public checkout). Integrated
 * billing embeds the same form inline on /integrated instead.
 */
export function StripeCheckoutPage() {
  const { t, i18n } = useTranslation();
  const [, params] = useRoute<{ id: string }>("/stripe/checkout/:id");
  const [, setLocation] = useLocation();
  const orderId = params?.id;

  const [payload, setPayload] = useState<StripeCheckoutPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [backUrl, setBackUrl] = useState("/plans");

  useEffect(() => {
    if (!orderId) return;
    setBackUrl(resolveBackUrl(orderId));

    void (async () => {
      const cached = loadStripeCheckoutSession(orderId);
      if (cached) {
        setPayload({
          order_id: orderId,
          client_secret: cached.clientSecret,
          publishable_key: cached.publishableKey,
          final_cents: cached.finalCents,
          renewal_cents: cached.renewalCents,
          first_charge_at: cached.firstChargeAt,
          interval: cached.interval,
          // Only the public checkout caches the renewal; the integrated flow leaves it out.
          context_kind: cached.renewalCents != null ? "new_customer" : undefined,
        });
        setBackUrl(resolveBackUrl(orderId));
        return;
      }

      try {
        const data = await api<StripeCheckoutPayload>(`/api/public/orders/${orderId}/stripe-checkout`);
        setBackUrl(resolveBackUrl(orderId, data));
        if (!data.publishable_key || !data.client_secret) {
          console.error("[zenpay] stripe-checkout.load", {
            reason: "missing_publishable_key_or_client_secret",
            orderId,
            hasPublishableKey: Boolean(data.publishable_key),
            hasClientSecret: Boolean(data.client_secret),
          });
          setError("stripe_not_configured");
          return;
        }
        saveStripeCheckoutSession(orderId, {
          clientSecret: data.client_secret,
          publishableKey: data.publishable_key,
          finalCents: data.final_cents,
          ...(data.context_kind === "new_customer"
            ? { renewalCents: data.renewal_cents, interval: data.interval, firstChargeAt: data.first_charge_at }
            : {}),
        });
        setPayload(data);
      } catch (err) {
        logApiError(`stripe-checkout.load orderId=${orderId}`, err);
        setLoadError(err);
        try {
          const order = await api<{
            id: string;
            plan?: Plan;
            interval?: BillingInterval;
            email?: string;
          }>(`/api/public/orders/${orderId}`);
          setBackUrl(
            resolveBackUrl(orderId, {
              order_id: order.id,
              client_secret: "",
              publishable_key: null,
              final_cents: 0,
              plan: order.plan,
              interval: order.interval,
              email: order.email,
            }),
          );
        } catch (orderErr) {
          logApiError(`stripe-checkout.load.order-fallback orderId=${orderId}`, orderErr);
        }
        setError("load_failed");
      }
    })();
  }, [orderId]);

  const showBack = useMemo(() => Boolean(orderId), [orderId]);

  function goToSuccess() {
    if (!orderId) return;
    setLocation(`/success?order=${orderId}&identified=1`);
  }

  return (
    <main className="min-h-screen surface-aurora">
      <div className="mx-auto max-w-lg px-4 py-8">
        {showBack && (
          <button
            type="button"
            onClick={() => setLocation(backUrl)}
            className="inline-flex items-center gap-1.5 text-sm opacity-80 hover:opacity-100 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("common.back")}
          </button>
        )}

        <div className="card-glass p-6 text-[color:var(--text-primary)]">
          <h1 className="text-xl font-semibold">{t("stripeCheckout.title")}</h1>
          {payload && (
            <p className="text-sm text-ink-2 mt-1 mb-4">
              {t("stripeCheckout.amount", { amount: formatMoney(payload.final_cents, i18n.language) })}
            </p>
          )}
          {payload?.context_kind === "new_customer" && payload.renewal_cents != null && payload.interval && (
            <div className="mb-4">
              <RenewalTerms
                todayCents={payload.final_cents}
                renewalCents={payload.renewal_cents}
                interval={payload.interval}
                nextChargeAt={payload.first_charge_at}
              />
            </div>
          )}
          <p className="text-xs text-ink-2 mb-4">{t("stripeCheckout.walletsHint")}</p>

          {error && (
            <div className="text-sm text-danger py-8 text-center space-y-2">
              <p>
                {error === "stripe_not_configured"
                  ? t("stripeCheckout.notConfigured")
                  : error === "load_failed"
                    ? stripeCheckoutLoadErrorMessage(loadError, t)
                    : t("common.error")}
              </p>
              {import.meta.env.DEV && loadError != null && (
                <p className="text-xs text-ink-2 font-mono break-all">
                  {stripeCheckoutLoadErrorDetail(loadError) ??
                    (loadError instanceof Error ? loadError.message : String(loadError))}
                </p>
              )}
            </div>
          )}

          {!error && !payload && (
            <div className="text-sm text-ink-2 py-12 text-center">{t("common.loading")}</div>
          )}

          {payload?.publishable_key && payload.client_secret && (
            <div className="stripe-embedded-shell rounded-xl overflow-hidden bg-white min-h-[420px]">
              <StripeEmbeddedCheckout
                publishableKey={payload.publishable_key}
                clientSecret={payload.client_secret}
                onComplete={goToSuccess}
              />
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
